// What the editor sends ahead of "Generate" so the agent reads the advertiser's sources early
// (POST /ad-generation/preparations). The agent keys what it reads by each source, so a later
// "Generate" with the same sources finds them read; anything else it reads itself.

function absoluteUrl(value) {
  try {
    const url = new URL(String(value || ''))
    return ['http:', 'https:'].includes(url.protocol) && url.hostname.includes('.') ? url.toString() : null
  } catch {
    return null
  }
}

/**
 * One Facebook, Instagram or LinkedIn post, or one YouTube video: what the agent reads as a social
 * source (the same rule as social_urls.detect_social_platform; profiles and channels are refused).
 */
export function isSocialPost(value) {
  const address = absoluteUrl(value)
  if (!address) return false
  const url = new URL(address)
  const host = url.hostname.toLowerCase()
  const on = (domain) => host === domain || host.endsWith(`.${domain}`)
  const path = url.pathname.toLowerCase().split('/').filter(Boolean)
  // A marker segment with something after it, e.g. /p/<id>.
  const followed = (...markers) => path.slice(0, -1).some((segment) => markers.includes(segment))
  if (on('instagram.com')) return followed('p', 'reel', 'tv')
  if (host === 'fb.watch') return path.length > 0
  if (on('facebook.com')) {
    return followed('posts', 'reel', 'reels', 'videos') || (path[0] === 'share' && path.length >= 3)
      || ['story_fbid', 'fbid', 'v'].some((key) => url.searchParams.has(key))
  }
  if (on('linkedin.com')) return followed('posts') || (path[0] === 'feed' && path[1] === 'update')
  if (host === 'youtu.be') return path.length > 0
  if (on('youtube.com')) return followed('shorts', 'live', 'embed') || (path[0] === 'watch' && url.searchParams.has('v'))
  return false
}

/**
 * The preparation body for the form's sources, or null while there is nothing worth reading
 * (no advertiser, no source yet, or an address still being typed or one the agent refuses).
 */
export function preparationRequest(sources, adReference) {
  if (!sources?.advertiser_name) return null
  if (sources.website && !absoluteUrl(sources.website)) return null
  if (sources.social_post_url && !isSocialPost(sources.social_post_url)) return null
  const files = sources.source_files || []
  if (!sources.website && !sources.google_place_id && !sources.social_post_url && !files.length) return null
  return {
    adReference: adReference || null,
    ad_id: sources.ad_id || null,
    advertiser_name: sources.advertiser_name,
    language: sources.language,
    // Sent as it is, but never a reason to prepare again: the analysis it feeds runs only with
    // files or a post, and a briefing typed later is read by "Generate" itself.
    briefing_text: sources.briefing_text || null,
    website: sources.website ? absoluteUrl(sources.website) : null,
    google_place_id: sources.google_place_id || null,
    social_post_url: sources.social_post_url || null,
    source_files: files,
  }
}

/** Equal for two requests that would make the agent read the same sources. */
export function preparationKey(request) {
  if (!request) return null
  const { briefing_text: _briefing, source_files: files = [], ...fields } = request
  return JSON.stringify({
    ...fields,
    files: files.map((file) => [file.name, file.size, file.lastModified]),
  })
}
